import React, { useState, useEffect, useRef } from 'react';

interface RestTimerProps {
  restSeconds: number;
  onComplete: () => void;
  onSkip: () => void;
  onCancel: () => void;
  exerciseName: string;
  setNumber: number;
}

/**
 * RestTimer - Countdown timer for rest periods between sets
 * Provides visual countdown, notifications, and controls
 */
export const RestTimer: React.FC<RestTimerProps> = ({
  restSeconds,
  onComplete,
  onSkip,
  onCancel,
  exerciseName,
  setNumber
}) => {
  const [timeLeft, setTimeLeft] = useState(restSeconds);
  const [isRunning, setIsRunning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio for notification
  useEffect(() => {
    // Create a simple beep sound using Web Audio API
    const createBeepSound = () => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800; // 800Hz beep
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    };

    audioRef.current = { play: createBeepSound } as any;
  }, []);

  // Timer logic
  useEffect(() => {
    if (isRunning && !isPaused && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsRunning(false);
            // Play notification sound
            try {
              if (audioRef.current) {
                audioRef.current.play();
              }
              // Vibrate if supported (Telegram WebApp)
              if (window.Telegram?.WebApp?.HapticFeedback) {
                window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
              }
            } catch (error) {
              console.log('Audio/haptic feedback not available:', error);
            }
            onComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isPaused, timeLeft, onComplete]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = (): number => {
    return ((restSeconds - timeLeft) / restSeconds) * 100;
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const addTime = (seconds: number) => {
    setTimeLeft(prev => Math.max(0, prev + seconds));
  };

  const getTimerColor = (): string => {
    if (timeLeft <= 10) return 'text-red-600';
    if (timeLeft <= 30) return 'text-orange-600';
    return 'text-blue-600';
  };

  const getProgressColor = (): string => {
    if (timeLeft <= 10) return 'bg-red-500';
    if (timeLeft <= 30) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 text-center">
        {/* Header */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Отдых</h3>
          <p className="text-sm text-gray-600">
            {exerciseName} • Подход #{setNumber}
          </p>
        </div>

        {/* Timer Display */}
        <div className="mb-6">
          <div className={`text-6xl font-bold mb-2 ${getTimerColor()}`}>
            {formatTime(timeLeft)}
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div 
              className={`h-2 rounded-full transition-all duration-1000 ${getProgressColor()}`}
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          
          <div className="text-xs text-gray-500">
            {timeLeft === 0 ? 'Время вышло!' : `Осталось ${timeLeft} сек`}
          </div>
        </div>

        {/* Controls */}
        {timeLeft > 0 ? (
          <div className="space-y-3">
            {/* Pause/Resume */}
            <div className="flex justify-center space-x-2">
              <button
                onClick={togglePause}
                className={`px-4 py-2 rounded-lg font-medium ${
                  isPaused 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-yellow-600 text-white hover:bg-yellow-700'
                }`}
              >
                {isPaused ? '▶️ Продолжить' : '⏸️ Пауза'}
              </button>
            </div>

            {/* Time Adjustment */}
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => addTime(-15)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
                disabled={timeLeft <= 15}
              >
                -15с
              </button>
              <button
                onClick={() => addTime(15)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
              >
                +15с
              </button>
              <button
                onClick={() => addTime(30)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
              >
                +30с
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={onSkip}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700"
              >
                Пропустить
              </button>
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
            </div>
          </div>
        ) : (
          /* Timer Finished */
          <div className="space-y-3">
            <div className="text-2xl">🎉</div>
            <p className="text-green-600 font-medium">Отдых завершён!</p>
            <p className="text-sm text-gray-600">Можно делать следующий подход</p>
            
            <button
              onClick={onComplete}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700"
            >
              Продолжить тренировку
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestTimer;